import { inject, Injectable, isDevMode } from '@angular/core';
import { AutoRefreshTokenService, KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, provideKeycloak, UserActivityService, withAutoRefreshToken } from 'keycloak-angular';
import { filter, firstValueFrom, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { toHttpParams } from '../utils/globals';
import {
  AuthUserModel,
  CityModel,
  ComponentType,
  CountryModel,
  Gender,
  HasPermissionModel,
  IdentityResourceService,
  MaritalStatus,
  MembershipModel,
  OrganizationModel,
  PermissionKey,
  PermissionModel,
  PermissionResourceService,
  StoreBasicModel,
  StoreFullModel,
  UserBasicModel,
  UserProfileModel
} from '../vn-api';
import Keycloak from 'keycloak-js';
import { TranslocoService } from '@jsverse/transloco';
import { toObservable } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { HX_COMPONENT_NAME } from '../interface';
import * as Sentry from '@sentry/browser';

@Injectable({
  providedIn: 'root'
})
export class HxAuthService {
  private readonly keycloak = inject(Keycloak);
  private readonly http = inject(HttpClient);
  private readonly permissionResourceService = inject(PermissionResourceService);
  private readonly identityService = inject(IdentityResourceService);

  user!: AuthUserModel;

  setAuthUser(user: AuthUserModel) {
    this.user = user;
  }

  isOral(): boolean {
    return this.user.store?.cityId === 9;
  }

  isOrenburg(): boolean {
    return this.user.store?.cityId === 5;
  }

  hasStoreMembership(groupId: number): Promise<boolean> {
    return firstValueFrom(this.permissionResourceService.storeMembershipExists(groupId)).then(res => res.hasStoreMembership);
  }

  getCitiesByBrandId(brandId: number): Promise<CityModel[]> {
    return firstValueFrom(this.http.get<CityModel[]>(`/api/vn/identity/brands/${brandId}/cities`));
  }

  logout() {
    this.keycloak.logout();
  }

  keycloakToken() {
    if (!this.keycloak.token) {
      throw new Error('keycloak.token.notFound');
    }
    return this.keycloak.token;
  }

  getIdentity(): Promise<AuthUserModel> {
    return firstValueFrom(this.identityService.getInfo());
  }

  getOrganization(): Observable<OrganizationModel> {
    return this.http.get<OrganizationModel>('/api/vn/identity/organization');
  }

  getCountry(): Observable<CountryModel> {
    return this.http.get<CountryModel>('/api/vn/identity/country');
  }

  getUiLang(): string {
    return this.user.language?.toLowerCase() ?? (['us', 'pro'].includes(location.hostname.split('.')[location.hostname.split('.').length - 1]) ? 'en': 'ru');
  }

  setUiLang(lang: string): Promise<void> {
    if (lang) {
      lang = lang.toUpperCase();
    }
    return firstValueFrom(this.http.post<void>('/api/vn/identity/language', { language: lang }));
  }

  // достает пользователей из текущего магазина по компонентам
  getStoreUsersByComponents(params?: {
    components?: ComponentType[],
    limit?: number,
  }): Observable<UserBasicModel[]> {
    return this.http.get<UserBasicModel[]>(`/api/vn/identity/store-users`, {params: toHttpParams(params, true)});
  }

  saveProfile(req: {
    iin?: string;
    email?: string;
    lastname?: string;
    firstname?: string;
    patronymic?: string;
    address?: string;
    birthdate?: string; // iso date
    gender?: Gender;
    maritalStatus?: MaritalStatus;
    avatarFileId?: number;
    signFileId?: number,
  }): Observable<void> {
    return this.http.post<void>('/api/vn/identity/profile', req);
  }

  updatePassword(password: string): Observable<void> {
    return this.http.put<void>('/api/vn/identity/password', password);
  }

  getMemberships(params?: {
    includeStore?: boolean,
  }): Observable<GetMembershipsItemResponse[]> {
    return this.http.get<GetMembershipsItemResponse[]>('/api/vn/identity/memberships', {params: toHttpParams(params, true)});
  }

  getCityStores(): Observable<StoreBasicModel[]> {
    return this.http.get<StoreBasicModel[]>('/api/vn/identity/city-stores');
  }

  getComponentStores(): Promise<StoreFullModel[]> {
    return firstValueFrom(this.identityService.getComponentStores());
  }

  getUsers(params: { limit?: number, component?: ComponentType }): Observable<UserProfileModel[]> {
    return this.http.get<UserProfileModel[]>(`/api/vn/identity/store-user-profiles`, {params: toHttpParams(params, true)});
  }

  getPermissions(params: { permissions: PermissionKey[] }): Observable<PermissionModel[]> {
    return this.identityService.getPermissionsByKeys(params.permissions);
  }

  hasPermission(key: PermissionKey, groupId?: number): Promise<HasPermissionModel> {
    return firstValueFrom(this.permissionResourceService.hasPermission(key, groupId));
  }

  updateUserStore(storeId: number): Promise<void> {
    return firstValueFrom(this.identityService.updateUserStore(storeId));
  }
}

interface GetMembershipsItemResponse {
  membership: MembershipModel;
  group?: StoreBasicModel;
}

export const provideKeycloakFn = (cfg: {
  url: string;
  realm: string;
  clientId: string;
}) => provideKeycloak({
  config: cfg,
  initOptions: {
    onLoad: 'login-required' // 'check-sso',
    // onLoad: 'check-sso',
    // silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
  },
  features: [
    withAutoRefreshToken({
      onInactivityTimeout: 'none',
    }),
  ],
  providers: [AutoRefreshTokenService, UserActivityService]
});

export async function initKeycloak() {
  const kc = inject(Keycloak);
  const auth = inject(HxAuthService);
  const tr = inject(TranslocoService);
  const component = inject(HX_COMPONENT_NAME);
  const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  const ready = await firstValueFrom(toObservable(keycloakSignal).pipe(filter(event => event.type === KeycloakEventType.Ready), take(1)));
  const token = kc.token;
  console.info('[main] token exists', ready, token);
  if (token) {
    const params = {method: 'GET', headers: new Headers()};
    params.headers.append('Authorization', `Bearer ${token}`);
    if (isDevMode()) {
      params.headers.append('component', component);
    }
    return fetch(`/api/vn/identity/info`, params)
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error('auth.error');
        }
      })
      .then(response => {
        auth.setAuthUser(response);
        tr.setActiveLang(auth.getUiLang());
        Sentry.withScope(scope => scope.setUser({'username': auth.user.phone}));
        return true;
      }, (err: Error) => {
        console.log('err', err);
        alert('Попробуйте перезагрузить страницу Ctrl + R, если ошибка выходит то сообщите администратору');
      });
  }
  return undefined;
}

/*export function initKeycloak(keycloak: KeycloakService, auth: HxAuthService, env: any, tr: TranslocoService) {
  console.log('env', JSON.stringify(env));
  return () => keycloak.init({
    config: env,
    initOptions: {
      onLoad: 'login-required' // 'check-sso',
      // onLoad: 'check-sso',
      // silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
    },
    enableBearerInterceptor: true,
    bearerExcludedUrls: [
      '/assets',
      '/clients/public'
    ].concat(env.excludeUrls ?? []),
  })
    .then(res => keycloak.getToken())
    .then(token => {
      const params = {method: 'GET', headers: new Headers()};
      params.headers.append('Authorization', `Bearer ${token}`);
      return fetch(`/api/vn/identity/info`, params);
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error('auth.error');
      }
    })
    .then(response => {
      auth.setAuthUser(response);
      tr.setActiveLang(auth.getUiLang());
      Sentry.withScope(scope => scope.setUser({'username': auth.user.phone}));
      return true;
    }, (err: Error) => {
      console.log('err', err);
      alert('Попробуйте перезагрузить страницу Ctrl + R, если ошибка выходит то сообщите администратору');
    });
}*/
